import { type FormEvent, useEffect, useState } from 'react';

import {
  ApiError,
  type BrandBookAudience,
  type BrandBookSummary,
  type ConceptLedgerEntry,
  fetchBrandBook,
  fetchBrandBookAudience,
  fetchBrandBookBrand,
  fetchConceptLedger,
  MAX_MARKETING_CONTEXT_LENGTH,
  saveBrandBookAudience,
  saveBrandBookContext,
} from '../api.ts';
import { AdKitPanel } from '../AdKitPanel.tsx';
import { BrandPanel } from '../BrandPanel.tsx';
import { ContextItemsPanel } from '../ContextItems.tsx';
import { describeError, redirectIfUnauthorised, useAppData } from '../data.tsx';
import { HouseRulesPanel } from '../HouseRulesPanel.tsx';
import { Icon } from '../Icon.tsx';
import { ProductProfilePanel } from '../ProductProfile.tsx';
import { brandBookIdFromPath, useLocation } from '../router.tsx';
import { useToast } from '../toast.tsx';
import { Badge, EmptyState, Markdown, Notice, PageHeader, Panel, Segmented, Skeleton } from '../ui.tsx';
import { brandBookSource } from './BrandBooks.tsx';

/**
 * One brand book (US-012): its brand profile with the operator's corrections,
 * its ad kit (one-shot ads US-007), its house rules (one-shot ads US-013),
 * its context items (US-013), its marketing context, its product profile and
 * its audience (the ad bible US-010) —
 * everything a content made in it reads, on the one page where it is fixed.
 *
 * Each panel talks to its own `/api/brand-books/:id/…` route, so a save in one
 * never waits on, or overwrites, another.
 */
export function BrandBook() {
  const { pathname } = useLocation();
  const id = brandBookIdFromPath(pathname);
  const { repositories } = useAppData();
  const [book, setBook] = useState<BrandBookSummary | null>(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id === null) return;
    const controller = new AbortController();
    setBook(null);
    setMissing(false);
    fetchBrandBook(id, controller.signal)
      .then((found) => {
        setBook(found);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted || redirectIfUnauthorised(cause)) return;
        if (cause instanceof ApiError && cause.status === 404) setMissing(true);
        else setError(describeError(cause));
      });
    return () => controller.abort();
  }, [id]);

  const back = { href: '/brand-books', label: 'Brand books' };

  if (id === null || missing) {
    return (
      <div className="page">
        <PageHeader back={back} title="Brand book" />
        <EmptyState icon="paintbrush" title="No such brand book">
          <p>It may have been deleted. The list has every brand book there is.</p>
        </EmptyState>
      </div>
    );
  }

  if (book === null) {
    return (
      <div className="page">
        <PageHeader back={back} title="Brand book" />
        {error === null ? (
          <div className="panel">
            <div className="panel__body">
              <Skeleton lines={5} />
            </div>
          </div>
        ) : (
          <Notice kind="error">Could not load this brand book: {error}</Notice>
        )}
      </div>
    );
  }

  const repository =
    book.sourceType === 'repository' && book.repositoryId !== null
      ? repositories?.find((candidate) => candidate.id === book.repositoryId)
      : undefined;

  return (
    <div className="page">
      <PageHeader
        back={back}
        eyebrow="Brand book"
        title={book.name}
        subtitle={
          <>
            {brandBookSource(book)} ·{' '}
            {book.contentCount === 1 ? '1 content' : `${String(book.contentCount)} contents`} made in it
          </>
        }
      />
      <div className="stack">
        <BrandPanel brandBookId={book.id} {...(repository === undefined ? {} : { repository })} />
        <AdKitPanel brandBookId={book.id} name={book.name} />
        <HouseRulesPanel brandBookId={book.id} name={book.name} />
        <ContextItemsPanel brandBookId={book.id} name={book.name} />
        <MarketingContextPanel brandBookId={book.id} name={book.name} />
        <ProductProfilePanel brandBookId={book.id} name={book.name} defaultOpen />
        <AudiencePanel brandBookId={book.id} name={book.name} />
        <ConceptLedgerPanel brandBookId={book.id} />
      </div>
    </div>
  );
}

/**
 * The book's free-text marketing context: Markdown the planning and copy
 * prompts fold in beside the brand profile. Saved on its own, whole.
 */
function MarketingContextPanel({
  brandBookId,
  name,
}: {
  readonly brandBookId: string;
  readonly name: string;
}) {
  const toast = useToast();
  const [saved, setSaved] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [view, setView] = useState<'write' | 'preview'>('write');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchBrandBookBrand(brandBookId, controller.signal)
      .then((brand) => {
        setSaved(brand.marketingContext ?? '');
        setDraft(brand.marketingContext ?? '');
        setError(null);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted || redirectIfUnauthorised(cause)) return;
        setError(describeError(cause));
      });
    return () => controller.abort();
  }, [brandBookId]);

  // The server trims before measuring, so the count shown and the one it
  // refuses on are the same number.
  const length = draft.trim().length;
  const tooLong = length > MAX_MARKETING_CONTEXT_LENGTH;
  const dirty = saved !== null && draft.trim() !== saved.trim();

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (!dirty || tooLong) return;
    setSaving(true);
    const value = draft.trim();
    saveBrandBookContext(brandBookId, value === '' ? null : value)
      .then((result) => {
        setSaved(result.marketingContext ?? '');
        setDraft(result.marketingContext ?? '');
        toast.ok(
          result.marketingContext === null
            ? `Removed the marketing context of ${name}.`
            : `Saved the marketing context of ${name}.`,
        );
      })
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        toast.error(describeError(cause));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <Panel
      title="Marketing context"
      icon="comment"
      meta={
        saved === null ? null : saved.trim() === '' ? (
          <Badge tone="neutral">empty</Badge>
        ) : (
          <Badge tone="done">{`${saved.trim().length.toLocaleString()} characters`}</Badge>
        )
      }
    >
      {error !== null && <Notice kind="error">{error}</Notice>}
      {saved === null ? (
        <Skeleton lines={3} />
      ) : (
        <form className="form form--tight" onSubmit={onSubmit}>
          <div className="field">
            <div className="field__head">
              <label className="field__label" htmlFor={`marketing-context-${brandBookId}`}>
                Positioning, audience, words to use and avoid
              </label>
              <Segmented
                value={view}
                options={[
                  { value: 'write', label: 'Write' },
                  { value: 'preview', label: 'Preview' },
                ]}
                onChange={setView}
                ariaLabel="Marketing context view"
              />
            </div>
            {view === 'write' ? (
              <textarea
                id={`marketing-context-${brandBookId}`}
                className="field__input field__textarea"
                value={draft}
                onChange={(event) => {
                  setDraft(event.target.value);
                }}
                placeholder={
                  '## Positioning\n- We sell to solo founders, not to teams.\n- Never claim "AI-powered"; say what it does.'
                }
                rows={8}
                spellCheck={false}
              />
            ) : draft.trim() === '' ? (
              <p className="muted">Nothing written yet.</p>
            ) : (
              <Markdown text={draft} />
            )}
            <p className="field__hint">
              Folded into the planning and copy prompts of every content made in this brand book,
              beside its brand profile. Markdown, up to{' '}
              {MAX_MARKETING_CONTEXT_LENGTH.toLocaleString()} characters
              {length === 0 ? '' : ` (${length.toLocaleString()} used)`}.
            </p>
          </div>
          {tooLong && (
            <Notice kind="error">
              That is {length.toLocaleString()} characters; the marketing context takes at most{' '}
              {MAX_MARKETING_CONTEXT_LENGTH.toLocaleString()}.
            </Notice>
          )}
          <div className="field__actions">
            <button
              type="submit"
              className="button button--primary button--small"
              disabled={!dirty || tooLong || saving}
            >
              <Icon name={saving ? 'sync' : 'check'} />
              {saving ? 'Saving…' : 'Save marketing context'}
            </button>
          </div>
        </form>
      )}
    </Panel>
  );
}

/**
 * The audience bible the book names (the ad bible US-010): every job of its
 * contents is given it. A book nobody has chosen for is matched to its
 * product profile by the server, and the panel says so; choosing here — an
 * audience or "None" — is saved at once and no match overrules it.
 */
function AudiencePanel({ brandBookId, name }: { readonly brandBookId: string; readonly name: string }) {
  const toast = useToast();
  const [audience, setAudience] = useState<BrandBookAudience | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchBrandBookAudience(brandBookId, controller.signal)
      .then((found) => {
        setAudience(found);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted || redirectIfUnauthorised(cause)) return;
        setError(describeError(cause));
      });
    return () => controller.abort();
  }, [brandBookId]);

  const onChange = (value: string): void => {
    const audienceKey = value === '' ? null : value;
    setSaving(true);
    saveBrandBookAudience(brandBookId, audienceKey)
      .then((saved) => {
        setAudience(saved);
        const chosen = saved.audiences.find((option) => option.key === saved.audienceKey);
        toast.ok(
          chosen === undefined
            ? `${name} names no audience.`
            : `Every content of ${name} now gets the ${chosen.name} audience.`,
        );
      })
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        toast.error(describeError(cause));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const current =
    audience === null ? undefined : audience.audiences.find((option) => option.key === audience.audienceKey);
  // A key the active version no longer has stays visible, so the select never lies about what is stored.
  const unknownKey = audience !== null && audience.audienceKey !== null && current === undefined;

  return (
    <Panel
      title="Audience"
      icon="eye"
      meta={
        audience === null ? null : current === undefined ? (
          <Badge tone="neutral">none</Badge>
        ) : (
          <Badge tone="done">{audience.automatic ? `${current.name} · matched` : current.name}</Badge>
        )
      }
    >
      {error !== null && <Notice kind="error">{error}</Notice>}
      {audience === null ? (
        error === null && <Skeleton lines={2} />
      ) : (
        <div className="form form--tight">
          <div className="field">
            <label className="field__label" htmlFor={`audience-${brandBookId}`}>
              Who the contents of this brand book speak to
            </label>
            <select
              id={`audience-${brandBookId}`}
              className="field__input"
              value={audience.audienceKey ?? ''}
              disabled={saving}
              onChange={(event) => {
                onChange(event.target.value);
              }}
            >
              <option value="">None</option>
              {audience.audiences.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.name} ({option.language})
                </option>
              ))}
              {unknownKey && (
                <option value={audience.audienceKey ?? ''}>{audience.audienceKey} (not in the active guidelines)</option>
              )}
            </select>
            {current?.description !== undefined && current.description !== null && (
              <p className="field__hint">{current.description}</p>
            )}
            <p className="field__hint">
              {audience.audiences.length === 0
                ? 'The guidelines have no active version yet, so there is no audience to choose.'
                : 'Its audience bible — moments, cast, words and banned lines — is given to every job of every content made in this brand book.'}
            </p>
          </div>
          {audience.automatic && current !== undefined && (
            <Notice kind="info">
              Set automatically: the product profile&rsquo;s buyer, status quo or buyer words name{' '}
              {current.name}. Choose another audience, or None, to change it.
            </Notice>
          )}
        </div>
      )}
    </Panel>
  );
}

/**
 * The concept ledger (the ad bible US-018): the hand of every ad made in this
 * book, newest first, with its first frame in one line. The next deal steers
 * away from these cards, so a brand's next ad differs from its last ones.
 */
function ConceptLedgerPanel({ brandBookId }: { readonly brandBookId: string }) {
  const [entries, setEntries] = useState<readonly ConceptLedgerEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchConceptLedger(brandBookId, controller.signal)
      .then((found) => {
        setEntries(found);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted || redirectIfUnauthorised(cause)) return;
        setError(describeError(cause));
      });
    return () => controller.abort();
  }, [brandBookId]);

  return (
    <Panel
      title="Concept ledger"
      icon="tasklist"
      meta={entries === null ? null : <Badge tone="neutral">{String(entries.length)} made</Badge>}
    >
      {error !== null && <Notice kind="error">{error}</Notice>}
      {entries === null ? (
        error === null && <Skeleton lines={2} />
      ) : entries.length === 0 ? (
        <p className="field__hint">
          No ad of this brand book has been marked ready from a dealt hand yet. Each one that is lands here, and the
          next deal steers away from its cards.
        </p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Content</th>
              <th>Hand</th>
              <th>Frame 0</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.contentId}>
                <td>
                  <a href={`/content/${encodeURIComponent(entry.contentId)}`}>{entry.contentName}</a>
                </td>
                <td>
                  {entry.cards.map((card) => (
                    <div key={`${card.deck}:${card.id}`}>
                      {card.slot ?? card.deck}: <code>{card.id}</code>
                      {card.name === null ? '' : ` ${card.name}`}
                    </div>
                  ))}
                </td>
                <td>{entry.frame0 ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}
